import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';

export type PolicyDetail = Tables<'policies'> & {
  owner_profile: {
    id: string;
    full_name: string;
    email: string;
    credentials?: string | null;
  } | null;
  clinic: {
    name: string;
  } | null;
};

export type PolicyVersion = Tables<'policy_versions'> & {
  created_by_profile: {
    full_name: string;
    credentials?: string | null;
  } | null;
  rejected_by_profile: {
    full_name: string;
    credentials?: string | null;
  } | null;
  approved_by_profile: {
    full_name: string;
    credentials?: string | null;
  } | null;
  approved_by: string | null;
  approved_at?: string | null;
  is_substantive?: boolean;
  version_status?: 'pending_approval' | 'approved' | 'rejected';
  review_cadence_months?: number | null;
  calculated_next_review?: string | null;
  notes?: string | null;
  rejection_reason?: string | null;
  rejected_by?: string | null;
  rejected_at?: string | null;
};

export type PolicyAcknowledgement = Tables<'policy_acknowledgements'> & {
  acknowledged_by_profile: {
    full_name: string;
    email: string;
  } | null;
  policy_version: {
    version_number: number;
  } | null;
};

export function usePolicy(policyId: string | undefined) {
  return useQuery({
    queryKey: ['policy', policyId],
    queryFn: async () => {
      if (!policyId) throw new Error('Policy ID required');
      
      const { data, error } = await supabase
        .from('policies')
        .select(`
          *,
          owner_profile:profiles!policies_policy_owner_fkey(id, full_name, email, credentials),
          clinic:clinics!policies_clinic_id_fkey(name)
        `)
        .eq('id', policyId)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Policy not found');
      
      return data as PolicyDetail;
    },
    enabled: !!policyId,
  });
}

export function usePolicyVersions(policyId: string | undefined) {
  return useQuery({
    queryKey: ['policy-versions', policyId],
    queryFn: async () => {
      if (!policyId) return [];
      
      const { data, error } = await supabase
        .from('policy_versions')
        .select(`
          *,
          created_by_profile:profiles!policy_versions_created_by_fkey(full_name, credentials),
          rejected_by_profile:profiles!policy_versions_rejected_by_fkey(full_name, credentials),
          approved_by_profile:profiles!policy_versions_approved_by_fkey(full_name, credentials)
        `)
        .eq('policy_id', policyId)
        .order('version_number', { ascending: false });

      if (error) throw error;
      
      // Cast the data to include the new fields from the database
      return (data ?? []) as PolicyVersion[];
    },
    enabled: !!policyId,
  });
}

export function usePolicyAcknowledgements(policyId: string | undefined) {
  return useQuery({
    queryKey: ['policy-acknowledgements', policyId],
    queryFn: async () => {
      if (!policyId) return [];
      
      // First get all version IDs for this policy
      const { data: versions, error: versionsError } = await supabase
        .from('policy_versions')
        .select('id')
        .eq('policy_id', policyId);

      if (versionsError) throw versionsError;
      if (!versions?.length) return [];

      const versionIds = versions.map(v => v.id);

      const { data, error } = await supabase
        .from('policy_acknowledgements')
        .select(`
          *,
          acknowledged_by_profile:profiles!policy_acknowledgements_acknowledged_by_fkey(full_name, email),
          policy_version:policy_versions!policy_acknowledgements_policy_version_id_fkey(version_number)
        `)
        .in('policy_version_id', versionIds)
        .order('acknowledged_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as PolicyAcknowledgement[];
    },
    enabled: !!policyId,
  });
}
